import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Plus } from "lucide-react";
import PageHeader from "../../../components/layout/PageHeader";
import {
  Button,
  EmptyState,
  buttonClasses,
  failedEmptyState,
  notFoundEmptyState,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import DishScopeToggle from "../components/DishScopeToggle";
import MealOptionCard from "../components/MealOptionCard";
import MealOptionSummary from "../components/MealOptionSummary";
import MealSlotModal from "../components/MealSlotModal";
import MealSlotTabs from "../components/MealSlotTabs";
import PlanDetailSkeleton from "../components/PlanDetailSkeleton";
import PlanMacroSummary from "../components/PlanMacroSummary";
import PlanNameField from "../components/PlanNameField";
import SendPlanModal from "../components/SendPlanModal";
import {
  createMealOption,
  isDishComplete,
  isOptionEmpty,
  planMacros,
  sortMealsByTime,
  unfilledMeal,
} from "../planMapping";
import {
  createMissingOptions,
  flushOpenOptions,
  hasUnsavedWork,
  rowsOf,
  serverRows,
} from "../services/planAutosave";
import {
  MAX_PLAN_DAYS,
  PLAN_STRUCTURE_COPY,
  canAddPlanUnit,
  planUnitLabel,
} from "../planStructure";
import { usePlan } from "../hooks/usePlan";
import { useMealSlotTypes } from "../hooks/useMealSlotTypes";
import { useDishScope } from "../hooks/useDishScope";
// A deep import rather than the billing barrel: that barrel pulls in the
// checkout and result screens, and this page only needs to know whether the
// workspace is allowed to write anything.
import {
  lockedProps,
  useSubscriptionGate,
} from "../../billing/hooks/useSubscriptionGate";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import {
  activateMealPlan,
  addMealEntry,
  addMealSlot,
  fetchMealPlan,
  fetchMealPlanTotals,
  removeMealEntry,
  removeMealSlot,
  renameMealPlan,
  saveMealEntryItems,
  sendMealPlan,
  updateMealSlot,
} from "../services/mealPlansService";
import { getPlan } from "../services/plansService";
import { ApiError } from "../../../lib/http";
import { ROUTES } from "../../../routes/paths";
import { decodeRouteId } from "../../../routes/routeIds";
import type {
  MealOption,
  MealSlot,
  PlanOrigin,
  PlanRecord,
} from "../types/plans.types";

/** Which meal the dialog is open for, and whether it is being added or edited. */
type MealDialog = { mode: "add" } | { mode: "edit"; meal: MealSlot };

/** The plan list, for a plan opened from anywhere that did not say otherwise. */
const PLAN_LIST_ORIGIN: PlanOrigin = {
  to: ROUTES.createPlan,
  label: "Create plan",
};

/**
 * Where this plan was opened from, out of the router state that carried it.
 *
 * Checked rather than cast: the state is whatever the last navigation put
 * there, and a plan opened by its URL alone has none at all.
 */
const originOf = (state: unknown): PlanOrigin => {
  const origin = (state as { origin?: Partial<PlanOrigin> } | null)?.origin;
  return typeof origin?.to === "string" && typeof origin.label === "string"
    ? { to: origin.to, label: origin.label }
    : PLAN_LIST_ORIGIN;
};

/**
 * The options with nothing in them yet, which are the ones that open ready to
 * write: an option with no dish on it has nothing to read, so a read-only view
 * of it would be an empty table with a Modify button under it.
 */
const emptyOptionIds = (record: PlanRecord | undefined): Set<string> =>
  new Set(
    (record?.meals ?? [])
      .flatMap((meal) => meal.options)
      .filter((option) => !option.dishes.some(isDishComplete))
      .map((option) => option.id)
  );

/**
 * A plan, read and written.
 *
 * It opens as something to read — the dishes as plain text, what each comes
 * to, and what the day comes to. Modify turns one option into the form that
 * writes it, and Save turns it back. A plan is looked at far more often than
 * it is changed, and a screen of dropdowns is a poor way to look at one.
 *
 * An active plan is only ever read: it has gone to the client, so nothing on
 * it can be opened as a form — see `readOnly` below.
 *
 * The screen edits a local copy of the record and writes it back on Save,
 * which is what makes the option's Save button mean something. Structural
 * changes are the exception: adding, renaming or removing a meal goes to the
 * backend as it happens, since there is no Save button on the tab strip to
 * press, and so does adding or removing an option. What is written inside one
 * — the food rows — is still the browser's until there is an endpoint for it.
 *
 * Leaving the screen is the other Save. A card open as a form holds rows only
 * this tab has, and a click on the sidebar takes the page away without asking:
 * so the builder writes them on its way out, and coming back finds the plan as
 * it was left. See `autosave`.
 */
const PlanDetailPage: React.FC = () => {
  // Enciphered in the path — see `routeIds`. A token that does not decode is
  // "", which finds no plan and lands on the "Plan not found" screen below.
  const { planId: token = "" } = useParams();
  const planId = decodeRouteId(token);
  const navigate = useNavigate();
  const toast = useToast();
  const getToken = useTokenGetter();

  // The names a meal can take. Read here rather than in the dialog because
  // the tab strip needs the same list to know when the day has no names left
  // to add — one request between them rather than one each.
  const { options: slotTypes, status: slotTypesStatus } = useMealSlotTypes();

  const origin = originOf(useLocation().state);

  // `loadStatus`, not `status`: a bare `status` is `window.status`, which the
  // lint rules rightly refuse.
  const { plan, status: loadStatus, error, reload, save } = usePlan(planId);

  // Read before the returns below, since a hook cannot sit behind one.
  const phase = useLoadPhase(!plan && loadStatus === "loading");

  // Meal plans are one of the three things a subscription is what pays for, so
  // with none running the builder is a plan to read rather than one to write:
  // every way into writing is off, and each says why. The plan itself is drawn
  // in full — a dietitian whose fortnight has run out must still be able to see
  // what they built. See `useSubscriptionGate`.
  const gate = useSubscriptionGate();
  // One answer for the whole builder rather than a different verb per control:
  // the meal pills, the options and the two buttons at the top are all the same
  // act — changing this plan — and reading three wordings for it across one
  // screen would suggest three different rules.
  const writeGate = gate.props("change this plan");

  const [draft, setDraft] = useState<PlanRecord | undefined>(plan);
  const [activeMealId, setActiveMealId] = useState("");
  const [mealDialog, setMealDialog] = useState<MealDialog | null>(null);
  const [sending, setSending] = useState(false);
  /**
   * Whether an option or a day is being created right now.
   *
   * A ref as well as state, and the ref is what guards: two presses in the
   * same tick both read the state as it was before either of them, so a second
   * click on Add day while the first is still in flight would send a second
   * create. Both would then be answered against the list as it stood before
   * either — the backend would hold two new days and the screen would show
   * one, which is how a meal ends up refusing a day the page still offers.
   *
   * The state is only so the button can say it is working.
   */
  const addingEntry = useRef(false);
  const [adding, setAdding] = useState(false);
  // Which dishes the Dish field searches — the workspace's setting, which is
  // why the line under the padlock says it applies to every plan.
  const dishScope = useDishScope();
  // Which options are open as forms. Held as ids rather than worked out from
  // the option's contents each render, or the first dish typed into a new
  // option would close the form that was being typed into.
  const [editingOptionIds, setEditingOptionIds] = useState(() =>
    emptyOptionIds(plan)
  );

  // What the backend holds for each option, which is what an unsaved row is
  // measured against — see `serverRows`. Moved on by hand as each option is
  // saved rather than read off the store, and a ref rather than state because
  // nothing on screen is drawn from it.
  const savedRows = useRef(serverRows(plan));

  // The autosave below, as of the last render. The cleanup that runs it is
  // registered once, on the way in, and would otherwise be holding the plan as
  // it looked on the first render rather than the one being left.
  const autosaveRef = useRef<(() => void) | undefined>(undefined);

  // Whether leaving was announced already — Save as draft says so itself, and
  // the autosave has no reason to say it a second time.
  const announced = useRef(false);

  // Only a different plan replaces what is being edited. The stored record
  // gets a new identity on every save — including this screen's own — and
  // following that would throw away edits made since the last one.
  const loadedId = useRef(plan?.id);
  useEffect(() => {
    if (plan && plan.id !== loadedId.current) {
      loadedId.current = plan.id;
      savedRows.current = serverRows(plan);
      setDraft(plan);
      setEditingOptionIds(emptyOptionIds(plan));
    }
  }, [plan]);

  // The screen going away is a save, not a discard. Whatever was typed into an
  // open card is the browser's alone until that option is saved, so leaving on
  // a sidebar click — or a breadcrumb, or the back button — would take it with
  // it; this writes it back on the way out. Registered once so it is a
  // departure that runs it rather than a re-render.
  useEffect(() => () => autosaveRef.current?.(), []);

  // The plan is fetched now, so there is a moment before it is either here or
  // known not to be. Saying "not found" during it would be answering a
  // question nobody has asked yet — so the builder is drawn as blocks, and the
  // plan lands into a screen already the shape it will keep. Only once the
  // fetch has visibly stalled: a plan that opens quickly opens on itself.
  if (!draft && loadStatus === "loading") {
    return <PlanDetailSkeleton origin={origin} phase={phase} />;
  }

  // A request that fell over is not a plan that is gone: it is worth asking
  // again, and saying the plan no longer exists would be a lie.
  if (!draft && loadStatus === "failed") {
    return (
      <div className="h-full flex flex-col overflow-hidden px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
        <PageHeader
          title="Could not load this plan"
          breadcrumbs={[
            { label: origin.label, to: origin.to },
            { label: "Could not load this plan" },
          ]}
        />
        <div className="mt-5 flex-1 min-h-0 flex items-center justify-center rounded-md border border-line bg-white">
          <EmptyState
            {...(() => {
              const state = failedEmptyState(
                "We could not load this plan",
                <Button variant="outline" size="sm" onClick={reload}>
                  Try again
                </Button>
              );
              // The server's own words where it gave any: they say more about
              // what went wrong than the generic line ever could.
              return error ? { ...state, description: error } : state;
            })()}
          />
        </div>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="h-full flex flex-col overflow-hidden px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
        <PageHeader
          title="Plan not found"
          breadcrumbs={[
            { label: origin.label, to: origin.to },
            { label: "Plan not found" },
          ]}
        />
        <div className="mt-5 flex-1 min-h-0 flex items-center justify-center rounded-md border border-line bg-white">
          <EmptyState
            {...notFoundEmptyState(
              "That plan no longer exists",
              "It may have been deleted since this link was opened. Every other plan is unaffected.",
              // A link and not a button — this goes somewhere. See the note
              // on `buttonClasses`.
              <Link
                to={origin.to}
                className={buttonClasses({ variant: "secondary", size: "sm" })}
              >
                Back to{" "}
                {origin.label === PLAN_LIST_ORIGIN.label
                  ? "all plans"
                  : origin.label}
              </Link>
            )}
          />
        </div>
      </div>
    );
  }

  // Whether a meal's list is a list of options or a list of the week's days.
  // The backend's answer, settled when the plan was created and sent on every
  // response — see `PlanRecord.structure`. The same data either way; only
  // what it is called, and how many of them there may be, differ.
  const structure = draft.structure;
  const unit = PLAN_STRUCTURE_COPY[structure].unit;

  // Derived rather than kept in step by hand: a removed meal, or a plan that
  // arrived with none selected, still lands on something that exists.
  const activeMeal =
    draft.meals.find((meal) => meal.id === activeMealId) ?? draft.meals[0];

  /**
   * Whether the plan is only to be read.
   *
   * An active plan — `sent`, in this module's vocabulary — is the one the
   * client is eating from, and changing it under them is not something this
   * screen offers: it is issued, not still being written. So every control
   * that writes goes away rather than greying out — the pencil on a meal, the
   * button that adds one, Modify on an option, and with it the whole card that
   * adds and deletes rows. What is left is the day as it was sent, which is
   * the same reason the heading stops being renameable.
   */
  const readOnly = draft.status !== "draft";

  /**
   * Why the plan cannot go to the client yet, or nothing if it can.
   *
   * Sending is what the client reads, and the backend is only told about an
   * option's rows when that option is saved — so a plan is sendable when
   * every meal is filled in and nothing is still open as a form. A card left
   * open holds rows the backend has never seen, and sending around it would
   * put out a plan missing the very food that was just typed into it.
   *
   * Read as a sentence rather than a flag because a button that has gone grey
   * with no reason given reads as broken; this is what the tooltip says.
   */
  const sendBlockedReason = ((): string | undefined => {
    if (draft.meals.length === 0) {
      return "Add a meal before sending this plan.";
    }

    const unfilled = unfilledMeal(draft.meals);
    if (unfilled) {
      return `Every ${unit.toLowerCase()} in ${
        unfilled.name || "each meal"
      } needs at least one dish with its unit and quantity.`;
    }

    // Checked after the meals are filled in, so a blank card open as a form is
    // told what to write rather than told to save nothing. Not asked where no
    // card can be open: a leftover id would hold the send back for an option
    // nobody can see, let alone save.
    if (readOnly) return undefined;

    const unsaved = draft.meals.find((meal) =>
      meal.options.some((option) => editingOptionIds.has(option.id))
    );
    if (unsaved) {
      return `Save the ${unit.toLowerCase()} you are editing in ${
        unsaved.name || "this meal"
      } before sending the plan.`;
    }

    return undefined;
  })();

  /**
   * Writes back whatever was left open and unsaved, for a departure that
   * pressed nothing.
   *
   * Called as the screen closes — see the effect above — which is why none of
   * it waits on React: the page is already gone by the time these calls come
   * back, so what they answer with goes to the store the builder reads its plan
   * from rather than to state nothing is drawing. Coming back into the plan
   * then finds the rows where they were left.
   *
   * It says so, on the screen that was navigated to: something was saved
   * without being asked for, and a plan the dietitian believes they walked away
   * from unsaved is worth one line. That is what separates this from an option
   * saved by hand, which stays silent — the press was theirs, the card answers
   * it on screen, and there is nothing they did not already know.
   *
   * Not where the departure announced itself — Save as draft has already said
   * it. A refusal is always said out loud: the screen is gone, and the work
   * would otherwise be lost without a word.
   *
   * Nothing runs at all where there is nothing to save, so leaving a plan that
   * was only read costs no calls. A plan that is only *to* be read has no open
   * card either, and one the workspace has no subscription for could not be
   * written anyway — see `writeGate`.
   */
  const autosave = () => {
    if (readOnly || writeGate.disabled) return;

    const baseline = savedRows.current;
    const openIds = editingOptionIds;
    if (!hasUnsavedWork(draft, baseline, openIds)) return;

    const quietly = announced.current;

    flushOpenOptions(getToken, draft, baseline, openIds)
      .then(async ({ record, written, error }) => {
        // Stored whatever happened, and first: the rows are on the record
        // either way, so a plan whose calls were refused still reopens on what
        // was typed rather than on what it held before.
        save(record);

        if (error) {
          reportFailure("Could not save your last changes", error);
          return;
        }

        if (!quietly) {
          toast.success(
            "Changes saved",
            `Your last changes to ${record.name} are saved.`
          );
        }

        // Nothing went to the backend, so its figures are what they were: a
        // plan whose meals are all the browser's own is added up here.
        if (!written) return;

        // The day is worth more than the stored copy says, and the backend is
        // the one that says how much. Only onto the record just written: a plan
        // reopened in the meantime is being edited again, and this is a figure,
        // not something to overwrite a day's work with.
        try {
          const macros = await fetchMealPlanTotals(getToken, record.id);
          if (getPlan(record.id) === record) save({ ...record, macros });
        } catch (reason) {
          console.error("Plan totals failed to refresh:", reason);
        }
      })
      .catch((reason) =>
        reportFailure("Could not save your last changes", reason)
      );
  };

  // Read on the way out, so it is the plan as it was left that gets saved.
  autosaveRef.current = autosave;

  /** Edits the copy on screen. Nothing is stored until something saves it. */
  const edit = (next: PlanRecord) => setDraft(next);

  /** Edits it and stores it, for the changes with no Save button of their own. */
  const commit = (next: PlanRecord) => {
    setDraft(next);
    save(next);
  };

  // Every change to the day goes through here, so the meals are kept in the
  // order they are eaten no matter which one was just added or moved.
  const withMeals = (meals: MealSlot[]): PlanRecord => ({
    ...draft,
    meals: sortMealsByTime(meals),
  });

  /** The names already on the plan, bar the one the dialog is editing. */
  const takenMealNames = draft.meals
    .filter(
      (meal) => mealDialog?.mode !== "edit" || meal.id !== mealDialog.meal.id
    )
    .map((meal) => meal.name);

  const replaceMeal = (mealId: string, next: MealSlot): MealSlot[] =>
    draft.meals.map((meal) => (meal.id === mealId ? next : meal));

  const handleOptionChange = (option: MealOption) => {
    if (!activeMeal) return;
    edit(
      withMeals(
        replaceMeal(activeMeal.id, {
          ...activeMeal,
          options: activeMeal.options.map((current) =>
            current.id === option.id ? option : current
          ),
        })
      )
    );
  };

  /** Opens an option as a form, or closes it back to something to read. */
  const setEditing = (optionId: string, editing: boolean) =>
    setEditingOptionIds((current) => {
      const next = new Set(current);
      if (editing) next.add(optionId);
      else next.delete(optionId);
      return next;
    });

  /**
   * Follows the options that have just been created from the ids the browser
   * gave them to the backend's, so a card open as a form stays open on the
   * option it was opened on rather than closing under whoever is typing.
   */
  const followEditing = (createdIds: Map<string, string>) => {
    if (createdIds.size === 0) return;
    setEditingOptionIds((current) => {
      const next = new Set<string>();
      current.forEach((id) => next.add(createdIds.get(id) ?? id));
      return next;
    });
  };

  /**
   * Writes a meal's options back to the plan and stores it.
   *
   * The ones already on it are nothing to store: a save is what marks a plan
   * as changed in this sitting, and a call that was refused changed nothing.
   */
  const commitOptions = (meal: MealSlot, options: MealOption[]) => {
    if (options === meal.options) return;
    commit(withMeals(replaceMeal(meal.id, { ...meal, options })));
  };

  /**
   * `createMissingOptions`, with the cards that were open kept open: the ids
   * the browser gave them are the backend's afterwards, and a card is not to
   * close under whoever is typing into it. The creating itself is shared with
   * the autosave — see `planAutosave`.
   */
  const createOptions = async (
    meal: MealSlot
  ): Promise<{ options: MealOption[]; error?: unknown }> => {
    const { options, createdIds, error } = await createMissingOptions(
      getToken,
      structure,
      draft.id,
      meal
    );
    followEditing(createdIds);
    return { options, error };
  };

  const handleAddOption = async () => {
    if (!activeMeal) return;
    // A week and no more, on a day-based plan. The button is off at seven, so
    // an ordinary press never reaches this; a keyboard on a card still
    // settling can. See `MAX_PLAN_DAYS`.
    if (!canAddPlanUnit(structure, activeMeal.options.length)) return;
    // And one at a time — see `addingEntry`.
    if (addingEntry.current) return;

    // A meal the browser invented has nowhere on the backend to hang an
    // option off — the plan opened on it because the create answered with no
    // meals at all — so its options stay the browser's, as they always were.
    if (activeMeal.isLocal) {
      const option = createMealOption();
      // Straight into the form: an option is added in order to be written.
      setEditing(option.id, true);
      commitOptions(activeMeal, [...activeMeal.options, option]);
      return;
    }

    addingEntry.current = true;
    setAdding(true);
    try {
      const { options, error } = await createOptions(activeMeal);
      if (error) {
        commitOptions(activeMeal, options);
        reportFailure(`Could not add the ${unit.toLowerCase()}`, error);
        return;
      }

      try {
        // An option is created by where it sits; a day by the note under it
        // and the number the backend gives it. Which of the two this is is the
        // service's to know — see `addMealEntry`.
        const created = await addMealEntry(
          getToken,
          structure,
          draft.id,
          activeMeal.id,
          options.length
        );
        setEditing(created.id, true);
        commitOptions(activeMeal, [...options, created]);
      } catch (reason) {
        // Whatever was created on the way here is kept all the same.
        commitOptions(activeMeal, options);
        reportFailure(`Could not add the ${unit.toLowerCase()}`, reason);
        // A refusal usually means the meal is fuller than this screen thinks —
        // "A meal can have at most 7 days" to a page showing five. So the
        // screen goes and looks, rather than leaving an Add day 6 button on a
        // meal that already has seven. See `catchUpWithMeal`.
        catchUpWithMeal(activeMeal);
      }
    } finally {
      addingEntry.current = false;
      setAdding(false);
    }
  };

  /**
   * Puts on screen the entries the backend holds for a meal and this page does
   * not.
   *
   * Only ever adds. What is already on the page may be a card someone is
   * halfway through typing into, and none of that is the backend's to replace
   * — it has never been told about it. So this reads the plan back, keeps
   * everything that is here, and appends whatever is missing, which is enough
   * to make the count on screen true.
   *
   * Quiet if it cannot: the press has already been answered with the reason it
   * was refused, and a second message about a read nobody asked for would be
   * noise on top of it.
   */
  const catchUpWithMeal = async (meal: MealSlot) => {
    let stored: PlanRecord;
    try {
      stored = await fetchMealPlan(getToken, draft.id);
    } catch (reason) {
      console.error("Could not read the plan back:", reason);
      return;
    }

    const storedMeal = stored.meals.find((current) => current.id === meal.id);
    if (!storedMeal) return;

    const known = new Set(meal.options.map((option) => option.id));
    const missing = storedMeal.options.filter(
      (option) => !option.isLocal && !known.has(option.id)
    );
    if (missing.length === 0) return;

    const entries = [...meal.options, ...missing];
    // Days are read in the order they are eaten; options keep the order they
    // were added in, which is the order they are already in.
    if (structure === "day") {
      entries.sort((a, b) => (a.day ?? 0) - (b.day ?? 0));
    }

    // What the backend holds for each of them, so leaving the screen does not
    // send rows it has already got back to it — see `serverRows`.
    missing.forEach((option) => savedRows.current.set(option.id, rowsOf(option)));
    commitOptions(meal, entries);
  };

  const handleRemoveOption = async (optionId: string) => {
    if (!activeMeal) return;

    const removed = activeMeal.options.find(
      (option) => option.id === optionId
    );
    // Only an option the backend has is deleted there. The blank one it was
    // never told about is simply dropped, and nothing is asked of it.
    if (removed && !removed.isLocal) {
      try {
        await removeMealEntry(
          getToken,
          structure,
          draft.id,
          activeMeal.id,
          optionId
        );
      } catch (reason) {
        // Still on the plan, since it is still on the backend.
        reportFailure(`Could not remove the ${unit.toLowerCase()}`, reason);
        return;
      }
    }

    const remaining = activeMeal.options.filter(
      (option) => option.id !== optionId
    );
    // A meal is never left with nothing to write in. Taking the last option
    // out puts a blank one back, open as a form — the same state the meal was
    // added in, rather than a bare Add option button to press first.
    const replacement = remaining.length === 0 ? createMealOption() : undefined;

    setEditingOptionIds((current) => {
      const next = new Set(current);
      // The deleted option is gone; leaving its id behind would reopen a
      // later option that happened to be given the same one.
      next.delete(optionId);
      if (replacement) next.add(replacement.id);
      return next;
    });

    commitOptions(activeMeal, replacement ? [replacement] : remaining);
    // Said out loud, the way saving one is. The option leaving the page is
    // not an answer on its own: an option deleted from a meal of one comes
    // straight back as a blank one, which reads as nothing having happened.
    toast.success(
      `${unit} deleted`,
      `It has been removed from ${activeMeal.name || "this meal"}.`
    );
    // Food off the plan moves the day's figures as surely as food onto it.
    if (removed && !removed.isLocal) refreshTotals(draft.id);
  };

  /**
   * Saves an option: the option itself first if the backend has not got it,
   * then the table written into it.
   *
   * In that order because the rows are written against the option's own id,
   * and the blank option a meal opens with has none until it is created — so
   * a first Save is two calls, and every one after it is one. The rows go up
   * whole, and what comes back replaces what was on screen: the ids are the
   * backend's afterwards, and the macros are the figures the plan will read
   * back with.
   *
   * A plan whose meals are the browser's own has nowhere to send any of it,
   * and is stored the way it always was — see `handleAddOption`.
   */
  const handleSaveOption = async (optionId: string) => {
    const option = activeMeal?.options.find(
      (current) => current.id === optionId
    );

    if (!activeMeal || activeMeal.isLocal || !option) {
      save(draft);
      // Nothing said: the card turning back into something to read is the
      // answer, and it is on screen where the press was. See the note on the
      // backend path below.
      setEditing(optionId, false);
      return;
    }

    const index = activeMeal.options.indexOf(option);
    const { options, error } = await createOptions(activeMeal);
    commitOptions(activeMeal, options);

    if (error) {
      // Left open as the form it was written in, with everything typed into
      // it still there to try again with.
      reportFailure(`Could not save the ${unit.toLowerCase()}`, error);
      return;
    }

    // The same option, under the id the backend gave it.
    const saved = options[index] ?? option;

    try {
      // The table as it stands, whole, and the instructions under it. Which
      // of its rows the backend can be told about is the service's to decide
      // — a half-written one is not food on a plan, and one that names its
      // food in words alone is not something the endpoint could store.
      const contents = await saveMealEntryItems(
        getToken,
        structure,
        draft.id,
        activeMeal.id,
        saved.id,
        saved
      );

      commitOptions(
        activeMeal,
        options.map((current) =>
          current.id === saved.id ? { ...current, ...contents } : current
        )
      );
      // What the backend holds for this option now, so reopening it with
      // Modify and closing the screen again does not send the same rows twice.
      savedRows.current.set(saved.id, rowsOf({ ...saved, ...contents }));
    } catch (reason) {
      reportFailure(`Could not save the ${unit.toLowerCase()}`, reason);
      return;
    }

    // Back to something to read, which is what the option was saved to be —
    // and the whole of what a save says here.
    //
    // No toast. Writing a day's meals is a dozen of these presses, and a
    // message for each one is a stack of notices confirming what the card
    // already shows: the form is gone, the rows are typeset, the day's totals
    // have moved. A failure still speaks — that is the case the user cannot
    // see for themselves.
    setEditing(saved.id, false);
    // The day is worth more than it was a moment ago, and the backend is the
    // one that says how much.
    refreshTotals(draft.id);
  };

  /**
   * Asks the backend what the plan comes to now, and puts that across the top
   * of the page.
   *
   * Called after anything that changes what is on the plan — an option saved,
   * an option deleted, a meal removed — because none of those endpoints
   * answers with the day's totals, only with the thing that was changed.
   *
   * Only the four numbers are written back, deliberately: the request takes a
   * moment, and a row typed into another option while it was in flight is
   * still on screen and still the dietitian's. Nothing is said if it fails —
   * the figures simply stay as they were, which is a page a beat out of date
   * rather than an error over work that succeeded.
   */
  const refreshTotals = async (planId: string) => {
    try {
      const macros = await fetchMealPlanTotals(getToken, planId);
      setDraft((current) => (current ? { ...current, macros } : current));
    } catch (reason) {
      console.error("Plan totals failed to refresh:", reason);
    }
  };

  /** What a failed request tells the user, in the words the API used. */
  const reportFailure = (what: string, reason: unknown) => {
    toast.error(
      what,
      reason instanceof ApiError
        ? reason.message
        : "We could not reach the server. Check your connection and try again."
    );
  };

  /**
   * Adding a meal, and renaming or moving one, both go to the backend — a
   * meal is the backend's now, `id` and `sort_order` included. The plan is
   * only changed once it has answered: the dialog stays open on a failure, so
   * nothing on screen claims a meal that was never made.
   */
  const handleMealSave = async ({
    name,
    time,
    slotTypeId,
  }: {
    name: string;
    time: string;
    slotTypeId: number;
  }) => {
    if (!mealDialog) return;

    if (mealDialog.mode === "add") {
      try {
        const meal = await addMealSlot(getToken, draft.id, {
          meal_slot_type: slotTypeId,
          time_of_day: time,
        });
        // What it comes with opens as a form, for the same reason a freshly
        // added option does: a meal is added in order to be written. That is
        // the blank option the builder puts under an option-based meal, and
        // the day 1 the backend opens a day-based one with.
        meal.options.forEach((option) => setEditing(option.id, true));
        commit(withMeals([...draft.meals, meal]));
        setActiveMealId(meal.id);
        setMealDialog(null);
      } catch (reason) {
        reportFailure("Could not add the meal", reason);
      }
      return;
    }

    try {
      const updated = await updateMealSlot(
        getToken,
        draft.id,
        mealDialog.meal.id,
        { meal_slot_type: slotTypeId, time_of_day: time }
      );
      // The options stay as they were: this call is about the meal's name and
      // its place in the day, and the response carries none of what is
      // written underneath it.
      commit(
        withMeals(
          replaceMeal(mealDialog.meal.id, {
            ...mealDialog.meal,
            name: updated.name || name,
            time: updated.time || time,
            slotTypeId,
          })
        )
      );
      setMealDialog(null);
    } catch (reason) {
      reportFailure("Could not update the meal", reason);
    }
  };

  const handleMealRemove = async () => {
    if (mealDialog?.mode !== "edit") return;

    try {
      await removeMealSlot(getToken, draft.id, mealDialog.meal.id);
      commit(
        withMeals(draft.meals.filter((meal) => meal.id !== mealDialog.meal.id))
      );
      setMealDialog(null);
      // The same as an option's: a delete that took everything under it with
      // it is worth saying, not only showing.
      toast.success(
        "Meal removed",
        `${mealDialog.meal.name} is no longer on the plan.`
      );
      // A meal off the day takes every option under it, and its figures with
      // them.
      refreshTotals(draft.id);
    } catch (reason) {
      reportFailure("Could not remove the meal", reason);
    }
  };

  /**
   * Puts the plan away unfinished, which is the whole of what a draft is.
   *
   * An option still open as a form is no reason to hold this back: leaving the
   * screen writes it, the same as leaving by any other door — see `autosave`.
   * This is the one departure that says so itself, so the autosave keeps quiet
   * about it rather than following one message with another.
   */
  const handleSaveDraft = () => {
    announced.current = true;
    save({ ...draft, status: "draft" });
    // A sentence rather than the plan's own name at the head of one: a name is
    // whatever was typed into the heading — "anup sept plan" — and a message
    // that opens with it reads as a fragment.
    toast.success(
      "Saved as draft",
      `Your changes to ${draft.name} are saved. The plan has not gone to the client.`
    );
    // Back to the screen the plan was started from — the plan list, or the
    // client's own Plans tab. The same place sending it leaves for.
    navigate(origin.to);
  };

  /**
   * Renames the plan from its own heading.
   *
   * Stored as it happens, like the rest of the plan's structure — there is no
   * Save button on a page heading either. Only the name comes back, so
   * everything the builder is holding underneath it stays where it is.
   */
  const handleRename = async (name: string): Promise<boolean> => {
    try {
      const stored = await renameMealPlan(getToken, draft.id, name);
      commit({ ...draft, name: stored });
      toast.success("Plan renamed", `It is called ${stored} now.`);
      return true;
    } catch (reason) {
      // Answered `false`, which leaves the box open on what was typed: the
      // plan is still called what it was called.
      reportFailure("Could not rename the plan", reason);
      return false;
    }
  };

  /**
   * Activates the plan, emails it out, and only then treats it as sent.
   *
   * Two calls in that order, because the backend models them as two things: a
   * draft is activated into the plan the client holds, and the document is
   * mailed to the addresses given. Mailing a plan that is still a draft would
   * put out something the backend does not consider issued, so the activate
   * goes first and a refusal there stops the send from being attempted.
   *
   * Only a draft is activated — a plan that is already out is being copied to
   * another inbox, not issued again, and the endpoint has nothing to do.
   *
   * The backend is what sends it — the addresses go up in one call, and the
   * document that goes with them is the one the PDF endpoint renders. A call
   * that was refused leaves the dialog open on the addresses that were typed,
   * so the send can be tried again from what is already on screen.
   */
  const handleSendPlan = async (emails: string[]) => {
    if (draft.status === "draft") {
      try {
        await activateMealPlan(getToken, draft.id);
      } catch (reason) {
        // Still a draft, and nothing has gone out: the dialog stays as it is.
        reportFailure("Could not activate the plan", reason);
        return;
      }

      // Stored the moment the backend has done it, rather than waiting on the
      // send: the plan is active over there either way, and a send that falls
      // over must not leave the screen claiming otherwise. Held on the copy on
      // screen as well, so trying the send again only tries the send — the
      // plan is not a draft any more, and activating it twice is not a thing
      // the endpoint does.
      //
      // The record the endpoint answers with is not read back, for the reason
      // renaming does not read one: the builder is holding the day, and the
      // response would write over it.
      commit({ ...draft, status: "sent" });
    }

    let sentTo: string[];
    try {
      sentTo = await sendMealPlan(getToken, draft.id, emails);
    } catch (reason) {
      reportFailure("Could not send the plan", reason);
      return;
    }

    save({ ...draft, status: "sent" });
    setSending(false);
    // The addresses the backend accepted, which is what it actually went to.
    toast.success("Plan sent", `${draft.name} has gone to ${sentTo.join(", ")}.`);
    navigate(origin.to);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
      <PageHeader
        title={
          // Changed where it is read, and only while the plan is a draft —
          // once it has gone to the client, what they were sent is what it
          // is called.
          <PlanNameField
            name={draft.name}
            editable={draft.status === "draft"}
            gate={writeGate}
            onRename={handleRename}
          />
        }
        titleSuffix={
          <span className="flex items-center gap-2.5">
            <span className="text-[15px] 3xl:text-[18px] text-ink-subtle">
              ({draft.code})
            </span>
            {/* On either kind. A plan that says which of the two it is
                answers the question wherever it is opened — the pill is the
                only thing on the page that tells an option-based plan from a
                day-based one before its meals are read. */}
            <span className="rounded-full border border-[#5BD3E1] bg-[#EFFBFD] px-2.5 py-1 text-[12px] font-medium text-[#00424B]">
              {PLAN_STRUCTURE_COPY[structure].badge}
            </span>
          </span>
        }
        breadcrumbs={[
          { label: origin.label, to: origin.to },
          { label: draft.name },
        ]}
        actions={
          <DishScopeToggle
            scope={dishScope.scope}
            canLock={dishScope.canNarrow}
            onChange={(scope) =>
              dishScope.setScope(scope).catch((reason) => {
                reportFailure("Could not change the dish search", reason);
              })
            }
          />
        }
      />

      <div className="mt-5 flex-1 min-h-0 overflow-y-auto custom-scrollbar-overlay">
        <div className="flex flex-col gap-4">
          <section className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-line bg-white px-5 sm:px-6 py-4">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-soft text-[14px] font-medium text-brand-dark">
                {draft.clientName.charAt(0).toUpperCase()}
              </span>
              <span className="text-[16px] 3xl:text-[18px] font-medium text-ink">
                {draft.clientName}
              </span>
              <span className="rounded-full border border-[#5BD3E1] bg-[#EFFBFD] px-2.5 py-1 text-[12px] text-[#00424B]">
                {draft.planType}
              </span>
            </div>

            <div className="flex items-center gap-4">
              {/* Nothing to put away: the plan is out with the client, and
                  pressing this would file an issued plan back as a draft. */}
              {!readOnly && (
                <button
                  type="button"
                  // Turned back by hand rather than natively disabled, the same
                  // way Send plan is: the browser dispatches nothing to a
                  // disabled button, and this one answers a press with the
                  // reason it is off. Nothing else holds it back — a draft is
                  // unfinished by definition, and a card left open is written
                  // by the leaving rather than stood in the way of.
                  {...lockedProps(writeGate, handleSaveDraft)}
                  className={`px-6 py-2.5 text-sm 3xl:text-base rounded-[6px] text-ink transition-colors ${
                    writeGate.disabled
                      ? "cursor-not-allowed opacity-50"
                      : "hover:text-[#00C2CB] hover:bg-[#E6FAFB]"
                  }`}
                >
                  Save as draft
                </button>
              )}
              <Button
                size="md"
                // Only a plan the backend holds in full goes out. Saving it as
                // a draft is still allowed — that is what a draft is for.
                //
                // And only a workspace with a plan behind it sends anything at
                // all: sending is what puts the plan in the client's hands,
                // which is the whole of what is being paid for. That answer
                // leads, for the reason Save as draft's does.
                disabled={writeGate.disabled || !!sendBlockedReason}
                title={writeGate.title ?? sendBlockedReason}
                onClick={() => setSending(true)}
                // Pressed anyway, which is what someone does when a button has
                // gone grey and they cannot see why. The reason is said out
                // loud rather than left to a tooltip nobody hovered for.
                onDisabledClick={
                  writeGate.disabled
                    ? writeGate.onDisabledClick
                    : () =>
                        toast.warning(
                          "This plan is not ready to send",
                          sendBlockedReason
                        )
                }
              >
                Send plan
              </Button>
            </div>
          </section>

          {/*
            The backend's figures for the day, which is what it will render
            the client's PDF from and what the plan list shows in its four
            columns. Adding the rows up here is the fallback for a plan it has
            never seen — a seeded fixture, or one being written with no
            connection — and for the moment before the first refresh lands.
          */}
          <PlanMacroSummary macros={draft.macros ?? planMacros(draft.meals)} />

          <section className="rounded-md border border-line bg-white p-4 sm:p-5 3xl:p-6">
            <MealSlotTabs
              meals={draft.meals}
              activeMealId={activeMeal?.id ?? ""}
              onSelect={setActiveMealId}
              onEdit={(meal) => setMealDialog({ mode: "edit", meal })}
              onAdd={() => setMealDialog({ mode: "add" })}
              // Nothing left to add once the plan holds every name there is.
              addDisabled={
                slotTypes.length > 0 &&
                draft.meals.length >= slotTypes.length
              }
              readOnly={readOnly}
              gate={writeGate}
            />

            {activeMeal ? (
              <div className="mt-5 flex flex-col gap-4">
                {activeMeal.options.map((option, index) => (
                  // The heading belongs to the option rather than to either
                  // half of it, so it does not move when the two swap.
                  <div key={option.id}>
                    <p className="mb-2 text-[14px] 3xl:text-[15px] text-ink">
                      {planUnitLabel(structure, index + 1)}
                    </p>
                    {/* An active plan is read whatever the editing set says:
                        an option left open when the plan went out is closed
                        by the plan going out, not by a Save nobody pressed.
                        A day-based plan's days are read for the other reason
                        — see `readOnly`. */}
                    {!readOnly && editingOptionIds.has(option.id) ? (
                      <MealOptionCard
                        option={option}
                        index={index}
                        unitName={unit.toLowerCase()}
                        onChange={handleOptionChange}
                        onRemove={() => handleRemoveOption(option.id)}
                        onSave={() => handleSaveOption(option.id)}
                        // A card can still be found open with no plan behind
                        // the workspace: an option with nothing in it opens as
                        // a form on its own, so the two buttons that would
                        // store it carry the reason as well.
                        gate={writeGate}
                        // The only option, with nothing in it: deleting would
                        // put an identical blank one straight back.
                        removeDisabled={
                          activeMeal.options.length === 1 &&
                          isOptionEmpty(option)
                        }
                      />
                    ) : (
                      <MealOptionSummary
                        option={option}
                        gate={writeGate}
                        onModify={
                          readOnly
                            ? undefined
                            : () => setEditing(option.id, true)
                        }
                      />
                    )}
                  </div>
                ))}

                {/* Gone once the week is full, rather than greyed out: a
                    day-based plan is seven days by definition, and a disabled
                    "Add day 8" reads as something being withheld. An
                    option-based meal has no such ceiling — the alternatives
                    are however many the dietitian wants to offer. */}
                {!readOnly &&
                  canAddPlanUnit(structure, activeMeal.options.length) && (
                    <button
                      type="button"
                      {...lockedProps(writeGate, handleAddOption)}
                      // Natively disabled while one is being created, unlike
                      // the subscription lock beside it: there is nothing to
                      // explain and nothing to do but wait, and the press that
                      // would be swallowed is the very press that puts the
                      // page out of step with the backend. See `addingEntry`.
                      disabled={adding || undefined}
                      aria-busy={adding || undefined}
                      className={`flex w-full items-center justify-center gap-2 rounded-md border-[0.5px] border-[#5BD3E1] bg-[#F8FEFF] py-3.5 text-[14px] 3xl:text-[15px] text-brand-dark transition-colors ${
                        writeGate.disabled || adding
                          ? "cursor-not-allowed opacity-50"
                          : "hover:bg-brand-soft"
                      }`}
                    >
                      {!adding && <Plus className="h-4 w-4" />}
                      {adding
                        ? `Adding ${unit.toLowerCase()}…`
                        : `Add ${planUnitLabel(
                            structure,
                            activeMeal.options.length + 1
                          ).toLowerCase()}`}
                    </button>
                  )}

                {/* What stands in its place, so a full week says it is full
                    rather than simply running out of buttons. */}
                {!readOnly &&
                  !canAddPlanUnit(structure, activeMeal.options.length) && (
                    <p className="text-center text-[13px] 3xl:text-[14px] text-ink-subtle">
                      {activeMeal.name || "This meal"} is written for all{" "}
                      {MAX_PLAN_DAYS} days.
                    </p>
                  )}
              </div>
            ) : (
              <p className="mt-6 text-center text-[14px] text-ink-subtle">
                {readOnly
                  ? "This plan has no meals on it."
                  : "This plan has no meals yet. Add one to start writing it."}
              </p>
            )}
          </section>
        </div>
      </div>

      {sending && (
        <SendPlanModal
          plan={draft}
          onClose={() => setSending(false)}
          onSend={handleSendPlan}
        />
      )}

      {mealDialog && (
        <MealSlotModal
          meal={mealDialog.mode === "edit" ? mealDialog.meal : undefined}
          takenNames={takenMealNames}
          slotTypes={slotTypes}
          slotTypesStatus={slotTypesStatus}
          onClose={() => setMealDialog(null)}
          onSave={handleMealSave}
          onRemove={
            mealDialog.mode === "edit" ? handleMealRemove : undefined
          }
        />
      )}
    </div>
  );
};

export default PlanDetailPage;
